import type { OnlineContract } from './adminContractApi';
import {
  type StatusFilter,
  formatDateTime,
  getContractTypeTitle,
  getStatusLabel,
} from './adminContractUtils';
import './AdminContractManage.scss';

export type AdminContractListProps = {
  contracts: OnlineContract[];
  loading: boolean;
  error: string | null;
  deletingId: number | null;
  searchQuery: string;
  statusFilter: StatusFilter;
  onSearchQueryChange: (value: string) => void;
  onStatusFilterChange: (value: StatusFilter) => void;
  onSearch: () => void;
  onRefresh: () => void;
  onCreate: () => void;
  onDetail: (row: OnlineContract) => void;
  onEdit: (row: OnlineContract) => void;
  onDelete: (row: OnlineContract) => void;
};

export default function AdminContractList({
  contracts,
  loading,
  error,
  deletingId,
  searchQuery,
  statusFilter,
  onSearchQueryChange,
  onStatusFilterChange,
  onSearch,
  onRefresh,
  onCreate,
  onDetail,
  onEdit,
  onDelete,
}: AdminContractListProps) {
  return (
    <div className="admin-contract-manage">
      <header className="admin-contract-manage__header">
        <div>
          <h1 className="admin-contract-manage__title">온라인 계약 관리</h1>
          <p className="admin-contract-manage__subtitle">
            계약 내용을 등록하면 고객이 고객명·전화번호로 조회 후 동의·서명할 수 있습니다.
          </p>
        </div>
        <button
          type="button"
          className="admin-contract-manage__refresh-btn"
          onClick={onRefresh}
          disabled={loading}
        >
          새로고침
        </button>
      </header>

      {error && <p className="admin-contract-manage__error">{error}</p>}

      <div className="admin-contract-manage__panel">
        <div className="admin-contract-manage__board-head">
          <h2 className="admin-contract-manage__section-title">계약 목록</h2>
          <button
            type="button"
            className="admin-contract-manage__create-btn"
            onClick={onCreate}
          >
            + 계약 등록
          </button>
        </div>

        <div className="admin-contract-manage__toolbar">
          <input
            className="admin-contract-manage__search"
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            placeholder="고객명, 전화번호, 회사명, 계약명 검색"
          />
          <select
            className="admin-contract-manage__filter-select"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
          >
            <option value="ALL">전체 상태</option>
            <option value="pending">서명 대기</option>
            <option value="signed">서명 완료</option>
          </select>
          <button
            type="button"
            className="admin-contract-manage__search-btn"
            onClick={onSearch}
            disabled={loading}
          >
            검색
          </button>
        </div>

        {loading ? (
          <p className="admin-contract-manage__loading">불러오는 중…</p>
        ) : contracts.length === 0 ? (
          <p className="admin-contract-manage__empty">등록된 계약이 없습니다.</p>
        ) : (
          <div className="admin-contract-manage__table-wrap">
            <table className="admin-contract-manage__table">
              <thead>
                <tr>
                  <th>등록일</th>
                  <th>고객명</th>
                  <th>전화번호</th>
                  <th>계약 유형</th>
                  <th>계약명</th>
                  <th>상태</th>
                  <th>서명일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>{row.customer_name}</td>
                    <td>{row.phone}</td>
                    <td>{getContractTypeTitle(String(row.contract_type))}</td>
                    <td className="admin-contract-manage__project-cell">
                      {row.contract_name || '—'}
                    </td>
                    <td>
                      <span
                        className={`admin-contract-manage__status admin-contract-manage__status--${
                          row.status === 'signed' ? 'signed' : 'pending'
                        }`}
                      >
                        {getStatusLabel(String(row.status))}
                      </span>
                    </td>
                    <td>{formatDateTime(row.signed_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-contract-manage__action-btn"
                        onClick={() => onDetail(row)}
                      >
                        상세
                      </button>
                      {row.status !== 'signed' && (
                        <button
                          type="button"
                          className="admin-contract-manage__action-btn"
                          onClick={() => onEdit(row)}
                        >
                          수정
                        </button>
                      )}
                      <button
                        type="button"
                        className="admin-contract-manage__action-btn admin-contract-manage__action-btn--danger"
                        onClick={() => onDelete(row)}
                        disabled={deletingId === row.id}
                      >
                        {deletingId === row.id ? '삭제 중' : '삭제'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
